import React from 'react';
import MetaTags from '../../components/seo/MetaTags';

export default function PrivacyPolicy() {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 pt-20">
      <MetaTags
        title="Privacy Policy | DiversiCoin Bitcoin BTM Network"
        description="Learn about how we protect your privacy and handle your information when using DiversiCoin Bitcoin BTMs."
        url="https://www.DiversiCoin.cash/privacy-policy"
      />
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-gray-800 rounded-lg p-8">
          <h1 className="text-3xl font-bold text-white mb-6">Privacy Policy</h1>
          <div className="prose prose-invert">
            <p className="text-gray-300">Last updated: February 28, 2024</p>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">Our Commitment</h2>
              <p className="text-gray-300">
                At DiversiCoin, we take your privacy seriously. We collect and handle your information 
                in accordance with federal regulations while ensuring your Bitcoin transactions remain 
                secure and compliant.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">Information We Collect</h2>
              <p className="text-gray-300">To comply with federal regulations and provide our services, we collect:</p>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Government-issued ID for KYC verification</li>
                <li>Transaction details (amounts, wallet addresses)</li>
                <li>BTM location usage data</li>
                <li>Contact information (email, phone)</li>
                <li>Security camera footage at BTM locations</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">How We Use Your Information</h2>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Process your Bitcoin transactions</li>
                <li>Verify your identity (KYC/AML compliance)</li>
                <li>Maintain transaction records as required by law</li>
                <li>Protect against fraud and unauthorized access</li>
                <li>Provide customer support</li>
                <li>Send important service updates</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">Information Sharing</h2>
              <p className="text-gray-300">We only share your information with:</p>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Regulatory authorities (as required by law)</li>
                <li>Law enforcement (with valid legal requests)</li>
                <li>Service providers (for BTM operations)</li>
              </ul>
              <p className="text-gray-300 mt-4">
                We never sell your personal information or share it for marketing purposes.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">Data Security</h2>
              <p className="text-gray-300">We implement comprehensive security measures:</p>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Industry-standard encryption</li>
                <li>Secure BTM hardware</li>
                <li>Regular security audits</li>
                <li>Strict access controls</li>
                <li>24/7 BTM monitoring</li>
                <li>Two-factor authentication for administrative access</li>
                <li>Real-time transaction monitoring</li>
                <li>Independent third-party security audits</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">Data Retention</h2>
              <p className="text-gray-300">
                We retain transaction records and identification data for 5 years as required by federal 
                regulations. Security footage is retained for 90 days unless needed for investigation 
                purposes.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">Your Rights</h2>
              <p className="text-gray-300">You have the right to:</p>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Access your transaction history</li>
                <li>Request corrections to your information</li>
                <li>Receive a copy of your data</li>
                <li>File a complaint</li>
              </ul>
              <p className="text-gray-300 mt-4">
                In the event of a security breach affecting your personal information, we will notify you 
                within 72 hours via email and provide guidance on protecting your information.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">Transaction Information</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Transaction limits: $20 minimum to $10,000 maximum per day</li>
                <li>Pending transactions are monitored and resolved within 24 hours</li>
                <li>Failed transaction data is retained for 30 days for troubleshooting</li>
                <li>Transaction receipts are available immediately and stored for 5 years</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">Regulatory Compliance</h2>
              <p className="text-gray-300">We maintain strict compliance with:</p>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Bank Secrecy Act (BSA)</li>
                <li>Anti-Money Laundering (AML) laws</li>
                <li>Know Your Customer (KYC) requirements</li>
                <li>State money transmitter laws</li>
                <li>Texas Department of Banking regulations</li>
                <li>Currency Transaction Reporting (CTR) requirements</li>
                <li>Texas Money Transmitter License requirements</li>
                <li>Suspicious Activity Reporting (SAR) obligations</li>
              </ul>
              <p className="text-gray-300 mt-4">
                Our Money Transmitter License (MTL-12345) is issued by the Texas Department of Banking, 
                and we maintain all required bonds and insurance coverage.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">Updates to This Policy</h2>
              <p className="text-gray-300">
                We may update this policy to reflect changes in our practices or regulatory requirements. 
                Updates will be posted on this page with a revised date.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">Contact Us</h2>
              <p className="text-gray-300">
                For privacy-related questions or concerns, contact us at:{' '}
                <a href="mailto:support@diversicoin.cash" className="text-purple-400 hover:text-purple-300">
                  support@diversicoin.cash
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}